import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputBase,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DoseId } from '~/domain/changes/ids';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { StructureSet } from '~/domain/patient/structureset';
import { useDoseOutsideGrid } from '~/hooks/dose/useDoseOutsideGrid';
import { readDoseFactor } from './doseFactorEntry';
import { COMPACT_ITEM, COMPACT_SELECT } from './replanningControlSx';
import { useDoseSum } from './useDoseSum';
import { voxelPercent } from './voxelPercent';
import { doseLabel } from '~/domain/projections/factories';

interface DoseSumDialogProps {
    readonly open: boolean;
    readonly onClose: () => void;
    // The prior dose as shown on the target, and the structures it is shown with.
    readonly priorOnTarget?: DoseMatrix;
    readonly targetStructures?: StructureSet;
}

function WeightInput({
    value,
    onChange,
    disabled,
    testId,
    ariaLabel,
}: {
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    testId: string;
    ariaLabel: string;
}) {
    const valid = readDoseFactor(value).factor !== undefined;
    return (
        <InputBase
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            error={!valid}
            inputProps={{
                inputMode: 'decimal',
                'data-testid': testId,
                'aria-label': ariaLabel,
                'aria-invalid': !valid,
            }}
            sx={{
                width: 52,
                height: 26,
                px: 1,
                fontSize: 12,
                border: '1px solid',
                borderColor: valid ? 'divider' : 'error.main',
                borderRadius: 1,
            }}
        />
    );
}

export default function DoseSumDialog({
    open,
    onClose,
    priorOnTarget,
    targetStructures,
}: DoseSumDialogProps) {
    const intl = useIntl();
    const {
        priorDose,
        priorDisplayPercent,
        currentDoses,
        blockedReasonId,
        state,
        create,
        reset,
    } = useDoseSum(priorOnTarget, targetStructures);
    const [chosen, setChosen] = useState<DoseId | ''>('');
    const [priorWeightText, setPriorWeightText] = useState('1');
    const [currentWeightText, setCurrentWeightText] = useState('1');

    useEffect(() => {
        if (open) {
            reset();
            setChosen('');
            setPriorWeightText('1');
            setCurrentWeightText('1');
        }
    }, [open, reset]);

    const currentId = chosen || currentDoses[0]?.id || '';
    const current = currentDoses.find((dose) => dose.id === currentId);
    const outside = useDoseOutsideGrid(current?.dose, priorDose?.grid);
    const outsidePercent =
        outside?.status === 'measured'
            ? voxelPercent(
                  outside.outside.outsideVoxels,
                  outside.outside.plannedVoxels
              )
            : 0;
    const rescaled = [
        ...(priorDose && priorDisplayPercent !== 100
            ? [
                  {
                      id: priorDose.id,
                      label: doseLabel(priorDose),
                      percent: priorDisplayPercent,
                  },
              ]
            : []),
        ...(current && current.displayPercent !== 100
            ? [
                  {
                      id: current.id,
                      label: current.label,
                      percent: current.displayPercent,
                  },
              ]
            : []),
    ];
    const priorWeight = readDoseFactor(priorWeightText);
    const currentWeight = readDoseFactor(currentWeightText);
    const weightsValid =
        priorWeight.factor !== undefined && currentWeight.factor !== undefined;
    const weightRefusal = priorWeight.refusal ?? currentWeight.refusal;
    const summing = state.status === 'summing';
    const created = state.status === 'created';
    // Held until the dropped dose is known, so the user decides with it in view.
    const canCreate =
        !blockedReasonId &&
        !!current &&
        weightsValid &&
        !summing &&
        !created &&
        !!outside &&
        outside.status !== 'measuring';

    return (
        <Dialog
            open={open}
            onClose={summing ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="replanning-dose-sum-title"
            data-testid="replanning-dose-sum-dialog"
        >
            <DialogTitle id="replanning-dose-sum-title" sx={{ fontSize: 15 }}>
                <FormattedMessage id="replanning.doseSum.title" />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage id="replanning.doseSum.explanation" />
                    </Typography>
                    {priorDose && (
                        <Box display="flex" gap={1} alignItems="center">
                            <Box
                                color="text.secondary"
                                minWidth={96}
                                fontSize={12}
                            >
                                <FormattedMessage id="replanning.doseSum.prior" />
                            </Box>
                            <Box
                                flex={1}
                                minWidth={0}
                                fontSize={12}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {doseLabel(priorDose)}
                            </Box>
                            <WeightInput
                                value={priorWeightText}
                                onChange={setPriorWeightText}
                                disabled={summing || created}
                                testId="replanning-dose-sum-prior-weight"
                                ariaLabel={intl.formatMessage({
                                    id: 'replanning.doseSum.priorWeight',
                                })}
                            />
                        </Box>
                    )}
                    {currentDoses.length > 0 && (
                        <Box display="flex" gap={1} alignItems="center">
                            <Box
                                color="text.secondary"
                                minWidth={96}
                                fontSize={12}
                            >
                                <FormattedMessage id="replanning.doseSum.current" />
                            </Box>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={currentId}
                                    disabled={summing || created}
                                    onChange={(event) =>
                                        setChosen(event.target.value as DoseId)
                                    }
                                    inputProps={{
                                        'aria-label': intl.formatMessage({
                                            id: 'replanning.doseSum.current',
                                        }),
                                    }}
                                    data-testid="replanning-dose-sum-current"
                                    sx={COMPACT_SELECT}
                                >
                                    {currentDoses.map((dose) => (
                                        <MenuItem
                                            key={dose.id}
                                            value={dose.id}
                                            sx={COMPACT_ITEM}
                                        >
                                            {dose.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <WeightInput
                                value={currentWeightText}
                                onChange={setCurrentWeightText}
                                disabled={summing || created}
                                testId="replanning-dose-sum-current-weight"
                                ariaLabel={intl.formatMessage({
                                    id: 'replanning.doseSum.currentWeight',
                                })}
                            />
                        </Box>
                    )}
                    {priorDose && current && (
                        <Typography
                            fontSize={12}
                            data-testid="replanning-dose-sum-formula"
                        >
                            <FormattedMessage
                                id="replanning.doseSum.formula"
                                values={{
                                    priorWeight: priorWeightText,
                                    prior: doseLabel(priorDose),
                                    currentWeight: currentWeightText,
                                    current: current.label,
                                }}
                            />
                        </Typography>
                    )}
                    {rescaled.map(({ id, label, percent }) => (
                        <Alert
                            key={id}
                            severity="info"
                            variant="outlined"
                            data-testid="replanning-dose-sum-display-scaled"
                        >
                            <FormattedMessage
                                id="replanning.doseSum.displayScaled"
                                values={{
                                    label,
                                    percent: intl.formatNumber(percent, {
                                        maximumFractionDigits: 1,
                                    }),
                                }}
                            />
                        </Alert>
                    ))}
                    {outside?.status === 'measured' &&
                        outside.outside.outsideVoxels > 0 && (
                            <Alert
                                severity="warning"
                                variant="outlined"
                                data-testid="replanning-dose-sum-outside"
                            >
                                <FormattedMessage
                                    id="replanning.doseSum.outside"
                                    values={{
                                        percent: intl.formatNumber(
                                            outsidePercent,
                                            { maximumFractionDigits: 1 }
                                        ),
                                        maxGy: intl.formatNumber(
                                            outside.outside.maxOutsideGy,
                                            {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }
                                        ),
                                    }}
                                />
                            </Alert>
                        )}
                    {outside?.status === 'failed' && (
                        <Alert
                            severity="warning"
                            variant="outlined"
                            data-testid="replanning-dose-sum-outside-unknown"
                        >
                            <FormattedMessage id="replanning.doseSum.outsideUnknown" />
                        </Alert>
                    )}
                    {weightRefusal && (
                        <Typography
                            fontSize={11}
                            color="error.main"
                            data-testid="replanning-dose-sum-weight-refused"
                        >
                            <FormattedMessage
                                id={weightRefusal.id}
                                values={weightRefusal.values}
                            />
                        </Typography>
                    )}
                    {blockedReasonId && (
                        <Alert
                            severity="info"
                            variant="outlined"
                            data-testid="replanning-dose-sum-blocked"
                        >
                            <FormattedMessage id={blockedReasonId} />
                        </Alert>
                    )}
                    {summing && <LinearProgress />}
                    {state.status === 'failed' && (
                        <Alert
                            severity="error"
                            variant="outlined"
                            data-testid="replanning-dose-sum-failed"
                        >
                            <FormattedMessage id={state.messageId} />
                        </Alert>
                    )}
                    {created && (
                        <Alert
                            severity="success"
                            variant="outlined"
                            data-testid="replanning-dose-sum-created"
                        >
                            <FormattedMessage
                                id="replanning.doseSum.created"
                                values={{ label: state.label }}
                            />
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={summing}>
                    <FormattedMessage id={created ? 'close' : 'cancel'} />
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!canCreate}
                    onClick={() => {
                        if (
                            current &&
                            priorWeight.factor !== undefined &&
                            currentWeight.factor !== undefined
                        ) {
                            void create(
                                current.id,
                                priorWeight.factor,
                                currentWeight.factor
                            );
                        }
                    }}
                    data-testid="replanning-dose-sum-create"
                >
                    <FormattedMessage id="replanning.doseSum.create" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
